import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import api from "../app/api";
import { login, logout, user as setUserAction } from "../features/auth/slice";
import { subscribeSessionSync, broadcastLogout } from "../utils/sessionSync";
import resolveCompanyId from "../utils/resolveCompanyId";

const SESSION_REVALIDATE_INTERVAL_MS = 3 * 60 * 1000;
export const PUBLIC_PATHS = ["/login", "/signup", "/forgot", "/reset", "/confirmation", "/shareView", "/form-submission"];

export const isPublicPath = (
  pathname = typeof window !== "undefined" ? window.location.pathname : "",
  search = typeof window !== "undefined" ? window.location.search : "",
) => {
  const params = new URLSearchParams(String(search || "").replace(/^\?/, ""));
  if (params.has("resetPasswordToken")) return true;

  const current = String(pathname || "").toLowerCase().replace(/\/+$/, "") || "/";
  return PUBLIC_PATHS.some((path) => {
    const publicPath = path.toLowerCase().replace(/\/+$/, "") || "/";
    return current === publicPath || current.startsWith(`${publicPath}/`);
  });
};

const readAuthParamsFromLocation = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));

  return {
    token:
      urlParams.get("token") ||
      urlParams.get("jwt") ||
      hashParams.get("token") ||
      hashParams.get("jwt") ||
      "",
    session_key:
      urlParams.get("session_key") ||
      urlParams.get("sessionKey") ||
      hashParams.get("session_key") ||
      hashParams.get("sessionKey") ||
      "",
  };
};

const clearSession = (dispatch) => {
  dispatch(logout());
};

const validateSessionStatus = async ({ token, session_key }) => {
  if (!session_key) {
    return { isValid: Boolean(token), usedFallback: true };
  }

  try {
    const response = await api.post("/auth/session-status", { session_key }, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const status = Boolean(response?.data?.status);
    const result = response?.data?.result || {};
    return {
      isValid: status && result?.is_active !== false,
      usedFallback: false,
    };
  } catch {
    return { isValid: false, usedFallback: false };
  }
};

const hydrateUserFromToken = async (dispatch, token) => {
  const response = await api.get("/auth/user", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const loggedInUser = response?.data;

  if (!loggedInUser?._id) {
    return false;
  }

  const companyId =
    resolveCompanyId(loggedInUser.companyID) ||
    (loggedInUser.role === "company" ? resolveCompanyId(loggedInUser._id) : null);

  localStorage.setItem("_id", loggedInUser._id);
  if (companyId) {
    localStorage.setItem("companyID", companyId);
  } else {
    localStorage.removeItem("companyID");
  }

  dispatch(
    login({
      token,
      user: loggedInUser,
      permissions: loggedInUser.permissions || [],
      session: { session_key: localStorage.getItem("session_key") },
    }),
  );
  dispatch(setUserAction(loggedInUser));
  return true;
};

export default function useSessionBootstrap() {
  const dispatch = useDispatch();
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const isSessionCheckInFlight = useRef(false);
  const isRedirectingOut = useRef(false);

  const redirectToLogin = () => {
    if (isPublicPath()) {
      return;
    }
    if (isRedirectingOut.current) return;
    isRedirectingOut.current = true;
    clearSession(dispatch);
    window.location.replace("/login");
  };

  const validateAndHandleSession = async ({ token, session_key }) => {
    const validation = await validateSessionStatus({ token, session_key });
    if (!validation.isValid) {
      redirectToLogin();
      return false;
    }
    return true;
  };

  useEffect(() => {
    const bootstrapSession = async () => {
      isRedirectingOut.current = false;
      const { token: tokenFromUrl, session_key: sessionKeyFromUrl } = readAuthParamsFromLocation();

      let token = tokenFromUrl || localStorage.getItem("token") || "";
      let session_key = sessionKeyFromUrl || localStorage.getItem("session_key") || "";

      if (isPublicPath()) {
        if (token) {
          try {
            const isSessionValid = await validateSessionStatus({ token, session_key });
            if (isSessionValid.isValid) {
              await hydrateUserFromToken(dispatch, token);
            }
          } catch {
            // Stay on the public form/share page even if a leftover session is invalid.
          }
        }
        setIsBootstrapping(false);
        return;
      }

      if (!token) {
        redirectToLogin();
        return;
      }

      try {
        if (tokenFromUrl) {
          localStorage.setItem("token", tokenFromUrl);
          if (sessionKeyFromUrl) {
            localStorage.setItem("session_key", sessionKeyFromUrl);
          }
          const params = new URLSearchParams(window.location.search);
          params.delete("token");
          params.delete("session_key");
          params.delete("sessionKey");
          params.delete("jwt");
          const qs = params.toString();
          const cleanUrl = `${window.location.origin}${window.location.pathname}${qs ? `?${qs}` : ""}`;
          window.history.replaceState({}, document.title, cleanUrl);
          token = tokenFromUrl;
          session_key = sessionKeyFromUrl || session_key;
        }

        const isSessionValid = await validateAndHandleSession({ token, session_key });
        if (!isSessionValid) return;

        const isUserHydrated = await hydrateUserFromToken(dispatch, token);
        if (!isUserHydrated) {
          redirectToLogin();
          return;
        }
      } catch {
        redirectToLogin();
        return;
      } finally {
        setIsBootstrapping(false);
      }
    };

    bootstrapSession();
  }, [dispatch]);

  useEffect(() => {
    if (isBootstrapping) return undefined;

    const runRevalidation = async () => {
      if (isSessionCheckInFlight.current) return;
      if (isPublicPath()) return;

      const token = localStorage.getItem("token") || "";
      const session_key = localStorage.getItem("session_key") || "";

      if (!token) {
        redirectToLogin();
        return;
      }

      isSessionCheckInFlight.current = true;
      try {
        await validateAndHandleSession({ token, session_key });
      } finally {
        isSessionCheckInFlight.current = false;
      }
    };

    const onFocus = () => runRevalidation();
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") runRevalidation();
    };

    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = window.setInterval(runRevalidation, SESSION_REVALIDATE_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [isBootstrapping]);

  useEffect(() => {
    return subscribeSessionSync(() => {
      if (isPublicPath()) return;
      redirectToLogin();
    });
  }, []);

  return { isBootstrapping, broadcastLogout, redirectToLogin };
}

export { broadcastLogout, clearSession as clearAuthSession };
